import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { clearDashboardCache, prefetchDashboard } from '@/lib/dashboardCache'

export interface AuthClaims {
  user_id: string
  username: string
  client_id: string
  role: string
  /** Fork 7I — fetched property (not a JWT claim). True when butler-db
   *  has Phase 2 demographics coverage for this tenant's events. Optional
   *  for backward-compat with pre-7I responses. */
  demographics_flag?: boolean
}

export interface EventSummary {
  EventID: number
  EventName: string
  StartDate: string
  EndDate: string
  VenueCity?: string | null
  HasData?: boolean
}

function pickDefaultEvent(events: EventSummary[]): number {
  const today = new Date().toISOString().slice(0, 10)
  const isRunning = (e: EventSummary) =>
    e.StartDate && e.StartDate <= today && (!e.EndDate || e.EndDate >= today)

  // 1. Running event (always top priority — operator wants to see their live event)
  const running = events.filter(isRunning)
  if (running.length > 0) {
    const withData = running.filter(e => e.HasData)
    return (withData.length > 0 ? withData : running)[0].EventID
  }
  // 2. Most recent past event with data
  const pastWithData = events
    .filter(e => e.HasData && e.EndDate && e.EndDate < today)
    .sort((a, b) => (b.StartDate ?? '').localeCompare(a.StartDate ?? ''))
  if (pastWithData.length > 0) return pastWithData[0].EventID
  // 3. Any past event, then future, then first
  const past = events.filter(e => e.EndDate && e.EndDate < today)
    .sort((a, b) => (b.EndDate ?? '').localeCompare(a.EndDate ?? ''))
  if (past.length > 0) return past[0].EventID
  return events[0].EventID
}

/**
 * Short-lived MFA challenge state stashed between POST /auth/login and
 * POST /auth/verify-totp. Scope: in-memory only (Fork 14 hard-stop —
 * never localStorage / sessionStorage; the server caps this at 5 min
 * anyway). Cleared on verify success, logout, explicit cancel, or
 * expiry tick.
 */
export interface PendingMfa {
  token: string
  channel: 'sms' | 'totp'
  maskedPhone: string | null
  /** Unix ms wall-clock — used by the expiry timer + VerifySmsPage hint. */
  expiresAt: number
  /** Telegraphs Twilio-side failure so the verify page can switch to
   *  backup-code copy instead of "code coming soon". Only meaningful for
   *  `channel === 'sms'`. */
  smsSent: boolean
}

export interface LoginResult {
  ok: boolean
  error?: string
  /** Backend returned 200 with `mfa_required: true` — user needs a second factor. */
  mfaRequired?: boolean
  /** Channel the backend chose: 'sms' | 'totp'. Undefined when mfaRequired is false. */
  mfaChannel?: string
  /**
   * Short-lived pending token the backend hands out alongside `mfa_required:
   * true`. A later /auth/verify-totp (or /auth/verify-sms) call will redeem
   * it for a real session. Preserved here so the dedicated MFA-UI fork can
   * consume it without re-POSTing the username/password. Null when the
   * backend didn't return one (older server, misconfigured flow).
   */
  mfaPendingToken?: string | null
}

export interface VerifyMfaResult {
  ok: boolean
  error?: string
  /** True once the mfa_pending_token expired before the user could submit.
   *  LoginCard/VerifySmsPage use this to route back to /login with a
   *  "session expired — please sign in again" notice. */
  expired?: boolean
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  claims: AuthClaims | null
  events: EventSummary[]
  currentEventId: number | null
  setCurrentEventId: (id: number | null) => void
  login: (username: string, password: string) => Promise<LoginResult>
  logout: () => Promise<void>
  pendingMfa: PendingMfa | null
  verifyMfa: (code: string) => Promise<VerifyMfaResult>
  clearPendingMfa: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [claims, setClaims] = useState<AuthClaims | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [events, setEvents] = useState<EventSummary[]>([])
  const [currentEventId, setCurrentEventId] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = window.sessionStorage.getItem('butler-buddy:currentEventId')
    return stored ? Number(stored) || null : null
  })
  const [pendingMfa, setPendingMfa] = useState<PendingMfa | null>(null)

  // Codex P2 (Fork 14 re-review): session generation counter. Every
  // logout bumps this so any in-flight background probe / event fetch
  // started under the old generation is dropped before it calls
  // setClaims / setEvents. Without this, a delayed /auth/me that
  // started during the post-verify hydration path could re-authenticate
  // the app after the user clicked Sign out, undoing the logout until
  // the next page refresh.
  const authGenRef = useRef(0)

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch('/api/events', { credentials: 'same-origin' })
      if (res.ok) {
        const data = await res.json()
        const list: EventSummary[] = data.events ?? data ?? []
        setEvents(list)
        if (list.length > 0) {
          const bestDefault = pickDefaultEvent(list)
          setCurrentEventId((prev) => prev ?? bestDefault)
          void prefetchDashboard(bestDefault)
        }
      }
    } catch {
      // Event list unavailable — Dashboard will show what it can
    }
  }, [])

  // Y3 2026-04-19: mirror currentEventId into sessionStorage so ChatContext
  // can read it without taking a direct dependency on AuthContext (keeps
  // the provider tree order flexible and avoids a circular import / test-
  // provider-tree shuffle). The YoY resolver uses this as `seedEventId`
  // when the chat message doesn't mention an event by name.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (currentEventId != null) {
      window.sessionStorage.setItem('butler-buddy:currentEventId', String(currentEventId))
    } else {
      window.sessionStorage.removeItem('butler-buddy:currentEventId')
    }
  }, [currentEventId])

  const probe = useCallback(async () => {
    try {
      const res = await fetch('/auth/me', { credentials: 'same-origin' })
      if (res.ok) {
        const data = await res.json()
        setClaims({
          user_id: data.user?.id ?? data.user_id,
          username: data.user?.username ?? data.username,
          client_id: data.user?.client_id ?? data.client_id,
          role: data.user?.role ?? data.role,
          demographics_flag: data.user?.demographics_flag ?? false,
        })
        return true
      }
    } catch {
      // No session cookie — user needs to log in
    }
    return false
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const ok = await probe()
      if (!cancelled && ok) {
        await fetchEvents()
      }
      if (!cancelled) setIsLoading(false)
    })()
    return () => { cancelled = true }
  }, [probe, fetchEvents])

  // Fork 14 (2026-04-17): expiry watchdog for pendingMfa. The backend
  // rejects the mfa_pending_token after 5 min (see totp-routes.js
  // MFA_PENDING_TTL_SECONDS). If the user idles past that, clear the
  // stashed challenge so VerifySmsPage's route guard bounces them back
  // to /login instead of showing a stale code field. Re-arm on every
  // pendingMfa change so a manual resend (new token) resets the clock.
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current)
      expiryTimerRef.current = null
    }
    if (!pendingMfa) return
    const msUntilExpiry = pendingMfa.expiresAt - Date.now()
    if (msUntilExpiry <= 0) {
      setPendingMfa(null)
      return
    }
    expiryTimerRef.current = setTimeout(() => {
      setPendingMfa((current) => {
        if (!current) return null
        if (current.expiresAt > Date.now()) return current
        return null
      })
    }, msUntilExpiry)
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current)
        expiryTimerRef.current = null
      }
    }
  }, [pendingMfa])

  const login = useCallback(async (username: string, password: string) => {
    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ username, password }),
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        return { ok: false, error: data.error || data.message || 'Login failed' }
      }

      // Codex HIGH #1 (2026-04-17): the backend returns 200 with
      // { mfa_required: true, mfa_channel, mfa_pending_token, ... } for
      // SMS/TOTP users BEFORE issuing a real session. Prior code treated
      // that as authenticated — setting non-null claims from the missing
      // data.user and letting the UI act as signed-in with bogus claims.
      // Stay unauthenticated and surface the MFA state so the caller can
      // route the user appropriately.
      if (data.mfa_required) {
        const pendingToken =
          typeof data.mfa_pending_token === 'string' && data.mfa_pending_token.length > 0
            ? data.mfa_pending_token
            : null
        const channel: 'sms' | 'totp' | undefined =
          data.mfa_channel === 'sms' || data.mfa_channel === 'totp' ? data.mfa_channel : undefined

        // Fork 14 (2026-04-17): stash the challenge in-memory so the
        // dedicated VerifySmsPage can consume it. SMS is the only path
        // wired into the portal UI today — TOTP enrollment was disabled
        // in Fork 11 (f1795a8), so a TOTP response is a signal the
        // account was enrolled via the legacy surface. We still carry
        // LoginResult shape unchanged (LoginCard decides what to do),
        // and we only persist pendingMfa state for the SMS path so the
        // route guard doesn't accidentally bounce a TOTP user at the
        // sms-verify page.
        if (pendingToken && channel === 'sms') {
          const expiresIn =
            typeof data.expires_in_seconds === 'number' && data.expires_in_seconds > 0
              ? data.expires_in_seconds
              : 5 * 60
          setPendingMfa({
            token: pendingToken,
            channel: 'sms',
            maskedPhone: typeof data.masked_phone === 'string' ? data.masked_phone : null,
            expiresAt: Date.now() + expiresIn * 1000,
            smsSent: data.sms_sent !== false,
          })
        } else {
          // TOTP path or missing token — don't stash; LoginCard will
          // fall back to the graceful-block message so Brian can use
          // the legacy surface.
          setPendingMfa(null)
        }

        return {
          ok: false,
          mfaRequired: true,
          mfaChannel: channel,
          mfaPendingToken: pendingToken,
          error:
            channel === 'totp'
              ? 'Multi-factor authentication (authenticator app) is required for this account. ' +
                'The new portal does not yet verify a TOTP factor — please use the legacy login for now.'
              : undefined,
        }
      }

      setClaims({
        user_id: data.user?.id ?? data.user_id ?? '',
        username: data.user?.username ?? username,
        client_id: data.user?.client_id ?? data.client_id ?? '',
        role: data.user?.role ?? data.role ?? '',
      })

      await fetchEvents()
      return { ok: true }
    } catch {
      return { ok: false, error: 'Network error — is the backend running?' }
    }
  }, [fetchEvents])

  const verifyMfa = useCallback(async (code: string): Promise<VerifyMfaResult> => {
    const current = pendingMfa
    if (!current) {
      return { ok: false, error: 'No verification in progress — please sign in again.', expired: true }
    }
    if (current.expiresAt <= Date.now()) {
      setPendingMfa(null)
      return { ok: false, error: 'Verification code expired — please sign in again.', expired: true }
    }

    try {
      const res = await fetch('/auth/verify-totp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ mfa_pending_token: current.token, code }),
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        if (res.status === 401 && data.error === 'invalid_token') {
          // Token rejected — almost always expiry, but can also be a
          // tampered token. Either way the only recovery is re-login.
          setPendingMfa(null)
          return { ok: false, error: 'Session expired — please sign in again.', expired: true }
        }
        if (res.status === 401 && data.error === 'invalid_code') {
          return { ok: false, error: 'Wrong code — try again.' }
        }
        if (res.status === 429) {
          return {
            ok: false,
            error: 'Too many attempts. Wait a few minutes, then try again.',
          }
        }
        return { ok: false, error: data.message || data.error || 'Verification failed' }
      }

      // Codex P2 (Fork 14 review): hydrate claims directly from the
      // verify response instead of depending on a follow-up /auth/me
      // probe. The backend returns `{user:{id, username, client_id,
      // role}}` alongside `mfa_verified: true` and also sets the
      // butler_session HttpOnly cookie. Prior code cleared pendingMfa
      // then awaited probe(); when probe() failed (cookie race, network
      // blip, proxy quirk) the user got stranded — /home would redirect
      // back to /login after pendingMfa was already gone, forcing a
      // fresh MFA round-trip after entering a correct code. Now we
      // hydrate from the response, then fire probe() as a best-effort
      // refinement, and only clear pendingMfa AFTER claims are set so
      // the route guards observe isAuthenticated=true in the same tick.
      const hydratedClaims: AuthClaims | null =
        data.user && typeof data.user.id !== 'undefined'
          ? {
              user_id: String(data.user.id),
              username: String(data.user.username ?? ''),
              client_id: String(data.user.client_id ?? ''),
              role: String(data.user.role ?? ''),
            }
          : null
      if (hydratedClaims) {
        setClaims(hydratedClaims)
        setPendingMfa(null)
        // Best-effort: fetch events in the background. Skip the
        // redundant /auth/me probe — claims are already canonical from
        // the verify response, and an in-flight probe can race a
        // logout (Codex P2 Fork 14 re-review). Guard fetchEvents with
        // the session-gen counter so a late-resolving /api/events that
        // lands after Sign-out can't repopulate the event list for a
        // signed-out user.
        const gen = authGenRef.current
        void (async () => {
          try {
            const res = await fetch('/api/events', { credentials: 'same-origin' })
            if (!res.ok) return
            if (authGenRef.current !== gen) return
            const data = await res.json()
            if (authGenRef.current !== gen) return
            const list: EventSummary[] = data.events ?? data ?? []
            setEvents(list)
            if (list.length > 0) {
              const bestDefault = pickDefaultEvent(list)
              setCurrentEventId((prev) => prev ?? bestDefault)
              void prefetchDashboard(bestDefault)
            }
          } catch {
            /* non-fatal */
          }
        })()
        return { ok: true }
      }
      // Fallback path: verify response lacked a user object (older
      // server?). Fall back to the probe-first pattern. If probe fails
      // AFTER a successful verify, treat it as an auth failure and
      // KEEP pendingMfa so the user can retry without re-logging in.
      const ok = await probe()
      if (ok) {
        setPendingMfa(null)
        await fetchEvents()
        return { ok: true }
      }
      return {
        ok: false,
        error:
          'Signed in but could not load your profile — please try again in a moment.',
      }
    } catch {
      return { ok: false, error: 'Network error — please try again.' }
    }
  }, [pendingMfa, probe, fetchEvents])

  const clearPendingMfa = useCallback(() => {
    setPendingMfa(null)
  }, [])

  const logout = useCallback(async () => {
    // Codex P2 (Fork 14 re-review): bump the session generation BEFORE
    // clearing state. Any in-flight background probe / event fetch
    // started under the old generation observes the change and drops
    // its setClaims/setEvents call, so a slow /auth/me can't
    // re-authenticate the app after Sign-out.
    authGenRef.current += 1

    // Clear local state synchronously so the UI flips to signed-out
    // instantly — UX never blocks on the revocation request.
    setClaims(null)
    setEvents([])
    setCurrentEventId(null)
    setPendingMfa(null)
    clearDashboardCache()

    // Codex HIGH #2 (2026-04-17): ChatContext's WebSocket auth falls back
    // to a legacy `butler_token` read from sessionStorage/localStorage
    // when no HttpOnly cookie is present. If we don't clear it on logout,
    // the WS can reconnect as the signed-out user. Clear both storages
    // synchronously so no reconnect window survives.
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.removeItem('butler_token')
        window.localStorage?.removeItem('butler_token')
      }
    } catch {
      // Storage access can throw in private/locked-down modes — nothing
      // to do here, the server-side revocation below is the backstop.
    }

    // Server-side revocation, bounded by a 5s AbortSignal so a hung
    // backend cannot leave the UI wedged. Failure is logged (visible in
    // devtools and any error tracking) but non-fatal to the UX — local
    // state is already cleared. Dropped from .catch(() => {}) silence to
    // avoid Codex HIGH #2's "silent failure" regression.
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)
    try {
      const res = await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        signal: controller.signal,
      })
      if (!res.ok) {
        console.warn('[logout] server revocation returned status', res.status)
      }
    } catch (err) {
      console.warn('[logout] server revocation failed', err)
    } finally {
      clearTimeout(timeout)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: claims !== null,
        isLoading,
        claims,
        events,
        currentEventId,
        setCurrentEventId,
        login,
        logout,
        pendingMfa,
        verifyMfa,
        clearPendingMfa,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
